import React, { FC } from 'react';
import { TrashIcon } from '@heroicons/react/24/solid';
import { Variant } from 'shared/types/product';
import { LineItem } from 'shared/types/wishlist/LineItem';
import Button from 'components/commercetools-ui/atoms/button';
import Image from 'components/commercetools-ui/atoms/image';
import Link from 'components/commercetools-ui/atoms/link';
import OutOfStock from 'components/commercetools-ui/atoms/out-of-stock';
import Typography from 'components/commercetools-ui/atoms/typography';
import ProductVariantPrice from 'components/commercetools-ui/organisms/product/product-details/components/product-variant-price';
import BundleIcon from 'components/icons/bundle-icon';
import EbookIcon from 'components/icons/ebook-icon';
import PhysicalBookIcon from 'components/icons/physical-book-icon';
import { useFormat } from 'helpers/hooks/useFormat';
import { getDiscount } from 'helpers/utils/getDiscount';
import DiscountBadge from '../badge/DiscountBadge';

export interface WishlistItemProps {
  item: LineItem;
  onRemove: () => Promise<void>;
  onMoveToCart: () => Promise<void>;
}

const WishlistItem: FC<WishlistItemProps> = ({ item, onRemove, onMoveToCart }) => {
  const { formatMessage: formatWishlistMessage } = useFormat({ name: 'wishlist' });

  const { hasDiscount, discountPercentage } = getDiscount(item?.variant ?? ({} as Variant));

  return (
    <div className="flex max-w-full items-stretch justify-start gap-10 p-16 md:gap-15">
      <div className="w-125 shrink-0 bg-white p-12">
        <div className="relative size-full rounded-sm">
          {item?.variant?.images?.[0] ? (
            <Image src={item?.variant?.images?.[0]} suffix="small" style={{ objectFit: 'contain' }} fill />
          ) : (
            <Image
              className="w-full rounded-sm object-contain group-hover:opacity-75"
              src="/images/imagen.png"
              alt="Product coming soon image in cart"
            />
          )}
          <div className="absolute left-0 top-0 z-10 w-full">
            <div className="w-full text-center">
              {hasDiscount && <DiscountBadge discountPercentage={discountPercentage} />}
            </div>
            {/* <div className="w-full text-center">
              {!item.variant?.isOnStock && (
                <div className="mb-8">
                  <OutOfStock />
                </div>
              )}
            </div> */}
          </div>
        </div>
      </div>
      <div className="max-w-full grow overflow-hidden">
        <div className="flex items-center gap-6">
          {item?.productTypeKey === 'physical-book' ? (
            <div className="flex items-center gap-6">
              <PhysicalBookIcon className="size-24" />
              <Typography>Printed Book</Typography>
            </div>
          ) : item?.productTypeKey === 'ebook' ? (
            <div className="flex items-center gap-6">
              <EbookIcon className="size-24" />
              <Typography>eBook</Typography>
            </div>
          ) : item?.productTypeKey === 'bundle' ? (
            <div className="flex items-center gap-6">
              <BundleIcon className="size-24" />
              <Typography>Book & eBook</Typography>
            </div>
          ) : (
            <div className="flex items-center gap-6">
              <Typography>
                {item?.productTypeKey
                  ?.replace(/-/g, ' ')
                  .split(' ')
                  .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
                  .join(' ')}
              </Typography>
            </div>
          )}
        </div>
        <div className="mt-2 flex items-center justify-between">
          <Link
            link={item._url}
            className="line-clamp-2 max-w-[90%] overflow-hidden text-ellipsis text-xs font-bold leading-normal text-blue-600 underline-offset-4 hover:underline focus:underline active:underline md:mt-6 md:text-base lg:text-lg"
            title={item.name}
          >
            {item.name}
          </Link>
        </div>

        <div className="relative mt-2 flex flex-wrap pr-40 text-base font-normal leading-none text-gray-500">
          {item?.variant?.attributes?.['author'] && (
            <>
              <Typography className="mr-4 text-xs md:text-sm">Author:</Typography>
              <Typography className="mr-8 whitespace-nowrap border-r border-gray-500 pr-8 text-xs text-blue-600 md:text-sm">
                {item?.variant?.attributes?.['author']}
              </Typography>
            </>
          )}
          {(item?.variant?.attributes?.['publicationYear'] ||
            item?.variant?.attributes?.['publicationMonth'] ||
            item?.variant?.attributes?.['publicationDay']) && (
            <>
              <Typography className="mr-4 whitespace-nowrap text-xs md:text-sm">Publication date: </Typography>
              <Typography className="whitespace-nowrap text-xs text-primary-black md:text-sm">
                {[
                  `${item?.variant?.attributes?.['publicationYear']}`,
                  item?.variant?.attributes?.['publicationMonth'] && item?.variant?.attributes?.['publicationMonth'],
                  item?.variant?.attributes?.['publicationDay'] && item?.variant?.attributes?.['publicationDay'],
                ]
                  .filter(Boolean)
                  .join('-')}
              </Typography>
            </>
          )}
        </div>
        {!item?.variant?.isOnStock && (
          <div className="mt-8 text-xs md:text-sm">
            <OutOfStock />
          </div>
        )}

        <div className="flex gap-8 pt-14">
          <ProductVariantPrice variant={item?.variant} inModalVersion className="text-12 md:text-base" />
        </div>
        <div className="mt-16 flex items-center justify-between leading-normal">
          <Button
            variant="primary"
            onClick={onMoveToCart}
            disabled={!item.variant?.isOnStock}
            className="m-4 py-8 text-14"
          >
            {formatWishlistMessage({ id: 'wishlist.add.to.cart', defaultMessage: 'Add to cart' })}
          </Button>
          <TrashIcon onClick={onRemove} className="size-24 cursor-pointer text-gray-500 hover:text-gray-900" />
        </div>
      </div>
    </div>
  );
};

export default WishlistItem;
